import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Key, AlertCircle, CheckCircle, Shield, Settings, WifiOff, Package, Monitor, Loader2 } from "lucide-react";
import axios from "axios";

export default function LicensePage() {
  const [licenseKey, setLicenseKey] = useState("");
  const [status, setStatus] = useState(null);
  const [validating, setValidating] = useState(false);
  const [deviceId, setDeviceId] = useState("");

  // Generate or retrieve device fingerprint
  useEffect(() => {
    let did = localStorage.getItem("vibez_device_fingerprint");
    if (!did) {
      did = "vibez-" + Math.random().toString(36).substring(2, 15) +
            Math.random().toString(36).substring(2, 15);
      localStorage.setItem("vibez_device_fingerprint", did);
    }
    setDeviceId(did);
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get("/api/license/status");
      setStatus(res.data);
    } catch (e) {
      setStatus({ status: "unlicensed", tier: "evaluation", addons: {} });
    }
  };

  const handleValidate = async (e) => {
    e.preventDefault();
    if (!licenseKey.trim()) {
      toast.error("Please enter a license key");
      return;
    }
    setValidating(true);
    try {
      const res = await axios.post("/api/license/validate", {
        key: licenseKey.trim().toUpperCase(),
        fingerprint: deviceId,
      });
      toast.success("License validated successfully!");
      setStatus(res.data.status);
    } catch (err) {
      const msg = err.response?.data?.detail?.message || "Invalid license key";
      toast.error(msg);
    } finally {
      setValidating(false);
    }
  };

  const isUnlicensed = !status || status.status === "unlicensed" || status.status === "expired";
  const isEvaluation = status?.tier === "evaluation";
  const addons = status?.addons || {};

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Toaster richColors position="top-center" duration={4000} />
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 text-primary mb-4">
            <Key className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-bold text-foreground">RestoYaar License</h1>
          <p className="text-muted-foreground mt-2">
            {isUnlicensed
              ? "Enter your license key to unlock the full product"
              : "Your license is active"}
          </p>
        </div>

        {/* Status Card */}
        <div className={`rounded-xl border p-6 mb-6 ${
          isUnlicensed ? "border-destructive/30 bg-destructive/5" :
          isEvaluation ? "border-warning/30 bg-warning/5" :
          "border-green/30 bg-green/5"
        }`}>
          <div className="flex items-center gap-3 mb-4">
            {isUnlicensed ? (
              <AlertCircle className="w-6 h-6 text-destructive" />
            ) : isEvaluation ? (
              <WifiOff className="w-6 h-6 text-warning" />
            ) : (
              <CheckCircle className="w-6 h-6 text-green" />
            )}
            <div>
              <p className="font-medium text-foreground">
                {isUnlicensed ? "Unlicensed / Expired" :
                 isEvaluation ? "Evaluation Mode" :
                 "Licensed - Active"}
              </p>
              <p className="text-sm text-muted-foreground">
                Tier: <span className="capitalize">{status?.tier || "—"}</span>
                {status?.expires_at && (
                  <>
                    &nbsp;•&nbsp; Expires: {new Date(status.expires_at).toLocaleDateString()}
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Add-on status */}
          <div className="grid grid-cols-2 gap-3">
            <AddonBadge
              name="CaptainPad"
              icon={<Monitor className="w-4 h-4" />}
              enabled={addons.captainpad === true}
              isAddon={true}
            />
            <AddonBadge
              name="KDS"
              icon={<Package className="w-4 h-4" />}
              enabled={addons.kds === true}
              isAddon={true}
            />
          </div>
        </div>

        {/* License Input */}
        <form onSubmit={handleValidate} className="space-y-4">
          <div>
            <label htmlFor="license-key" className="block text-sm font-medium text-foreground mb-2">
              License Key
            </label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                id="license-key"
                type="text"
                value={licenseKey}
                onChange={(e) => setLicenseKey(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                placeholder="VIBEZ-XXXX-XXXX-XXXX"
                className="w-full pl-10 pr-4 py-3 border border-input rounded-xl bg-background text-foreground placeholder:text-muted-foreground/50 font-mono text-base focus:outline-none focus:ring-2 focus:ring-primary"
                maxLength={23}
                disabled={!isUnlicensed || validating}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Format: VIBEZ-ABCD-1234-EFGH (uppercase letters and numbers)
            </p>
          </div>

          <button
            type="submit"
            disabled={validating || !licenseKey.trim() || !isUnlicensed}
            className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {validating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Validating...
              </>
            ) : (
              "Validate License"
            )}
          </button>

          {!isUnlicensed && (
            <p className="text-center text-sm text-muted-foreground">
              License is active. <Link to="/admin" className="text-primary underline">Manage settings →</Link>
            </p>
          )}
        </form>

        {/* Device info */}
        <details className="mt-8 text-center">
          <summary className="text-xs text-muted-foreground cursor-pointer">
            Device Information
          </summary>
          <div className="mt-2 p-3 bg-muted/50 rounded-lg text-xs text-muted-foreground font-mono break-all">
            {deviceId}
          </div>
        </details>
      </div>
    </div>
  );
}

function AddonBadge({ name, icon, enabled, isAddon }) {
  return (
    <div className={`flex items-center gap-2 p-3 rounded-lg border ${
      enabled ? "bg-green/10 border-green/30 text-green" : "bg-muted/30 border-border text-muted-foreground"
    }`}>
      <div className={`p-1.5 rounded ${enabled ? "bg-green/20" : "bg-muted"}`}>
        {icon}
      </div>
      <div>
        <p className="font-medium text-sm">{name}</p>
        <p className="text-xs capitalize">
          {enabled ? "Active" : isAddon ? "Requires Add-on" : "Not Available"}
        </p>
      </div>
    </div>
  );
}